/**
 * Centralized query key factory for all TanStack Query keys.
 *
 * Every key used by useQuery / invalidateQueries should be defined here so
 * that SSE-driven cache updates and REST hooks share the same key space.
 */

export const queryKeys = {
  // -------------------------------------------------------------------------
  // REST-backed data
  // -------------------------------------------------------------------------

  equipment: {
    all: ['equipment'] as const,
    lists: () => [...queryKeys.equipment.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.equipment.lists(), filters] as const,
    details: () => [...queryKeys.equipment.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.equipment.details(), id] as const,
    byTag: (tagId: string) => [...queryKeys.equipment.all, 'byTag', tagId] as const,
  },

  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
  },

  departments: {
    all: ['departments'] as const,
    lists: () => [...queryKeys.departments.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.departments.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.departments.all, 'detail', id] as const,
  },

  locations: {
    all: ['locations'] as const,
    lists: () => [...queryKeys.locations.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.locations.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.locations.all, 'detail', id] as const,
  },

  sessions: {
    all: ['sessions'] as const,
    active: () => [...queryKeys.sessions.all, 'active'] as const,
    activeList: (filters: object) => [...queryKeys.sessions.active(), filters] as const,
    history: () => [...queryKeys.sessions.all, 'history'] as const,
    historyList: (filters: object) => [...queryKeys.sessions.history(), filters] as const,
  },

  statuses: {
    all: ['status'] as const,
    lists: () => [...queryKeys.statuses.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.statuses.all, 'detail', id] as const,
  },

  topology: {
    all: ['topology'] as const,
    data: () => [...queryKeys.topology.all, 'data'] as const,
  },

  emergency: {
    all: ['emergency'] as const,
    active: () => [...queryKeys.emergency.all, 'active'] as const,
  },

  repeaters: {
    all: ['repeaters'] as const,
    list: () => [...queryKeys.repeaters.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.repeaters.all, 'detail', id] as const,
  },

  gateway: {
    all: ['gatewayStatus'] as const,
    status: () => [...queryKeys.gateway.all, 'current'] as const,
  },

  commands: {
    all: ['commands'] as const,
    detail: (id: string) => [...queryKeys.commands.all, id] as const,
    deferred: ['commands', 'deferred'] as const,
  },

  sites: {
    all: ['sites'] as const,
    list: () => [...queryKeys.sites.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.sites.all, 'detail', id] as const,
  },

  serviceEndpoints: {
    all: ['service-endpoints'] as const,
    list: () => [...queryKeys.serviceEndpoints.all, 'list'] as const,
  },

  systemHealth: {
    all: ['system-health'] as const,
  },

  systemSettings: {
    all: ['system-settings'] as const,
    list: () => [...queryKeys.systemSettings.all, 'list'] as const,
  },

  kioskAdmin: {
    all: ['kiosk-admin'] as const,
    lists: () => [...queryKeys.kioskAdmin.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.kioskAdmin.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.kioskAdmin.all, 'detail', id] as const,
  },

  kioskConfig: {
    byCode: (code: string) => ['kiosk-config', code] as const,
  },

  // -------------------------------------------------------------------------
  // Live state (written by LiveStateProvider via setQueryData)
  // -------------------------------------------------------------------------

  live: {
    radio: (dmrId: string) => ['live', 'radio', dmrId] as const,
    repeater: (id: string) => ['live', 'repeater', id] as const,
    repeaterIndex: ['live', 'repeater-index'] as const,
    activeRadioIds: ['live', 'active-radio-ids'] as const,
    emergencyAlerts: ['live', 'emergency-alerts'] as const,
    notifications: ['live', 'notifications'] as const,
    notificationCount: ['live', 'notification-count'] as const,
    eventLog: ['live', 'event-log'] as const,
    version: ['live', 'version'] as const,
  },
};
